"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: closingRef,
    isVisible: closingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const faqs = [{
    question: "How does VPNGENIX protect my data?",
    answer: "We utilize military-grade AES-256 encryption to secure your internet traffic. Our strict no-logs policy ensures that your browsing history, IP address, and connection timestamps are never recorded, stored, or shared with any third parties, keeping your digital footprint invisible."
  }, {
    question: "Can I use VPNGENIX on multiple devices?",
    answer: "Yes, a single VPNGENIX subscription allows you to connect up to 10 devices simultaneously. Whether you are using a smartphone, laptop, tablet, or router, our apps provide seamless protection across all your platforms without compromising speed or security."
  }, {
    question: "Does VPNGENIX slow down my internet?",
    answer: "Our optimized global server network is engineered for high-speed performance. By using the latest protocols like WireGuard, we minimize latency and bandwidth loss, ensuring you enjoy a fast, stable, and secure connection for streaming, gaming, and browsing."
  }, {
    question: "Is VPNGENIX easy to set up?",
    answer: "Setting up VPNGENIX is simple and takes less than two minutes. Just download our app, log in with your credentials, and click the connect button. Our intuitive interface handles all the complex configurations automatically, so you are protected instantly."
  }, {
    question: "Can I bypass geo-restrictions?",
    answer: "Absolutely. With servers in over 90 countries, VPNGENIX allows you to mask your location and access content from anywhere in the world. Bypass censorship and regional blocks to enjoy your favorite services and websites without any limitations."
  }, {
    question: "Do you offer a money-back guarantee?",
    answer: "We are confident in our service, which is why we offer a 30-day money-back guarantee. If you are not completely satisfied with your experience, contact our support team within the first month for a full refund, no questions asked."
  }, {
    question: "Is customer support available 24/7?",
    answer: "Our dedicated support team is available around the clock via live chat and email. Whether you have a technical question or need help with configuration, our experts are ready to assist you at any time, day or night, to ensure your peace of mind."
  }, {
    question: "Does VPNGENIX work on public Wi-Fi?",
    answer: "Public Wi-Fi networks are often insecure and prone to cyber threats. VPNGENIX creates a secure, encrypted tunnel for your data, protecting you from hackers and snoopers even when you are connected to unsecured hotspots at cafes, airports, or hotels."
  }];
  return <section data-section-id="4424" id="technicalclarificationsaccordion" className="bg-background text-foreground py-20 md:py-32 lg:py-40">
      <div className="container mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-32">
              <p className="font-default text-xs tracking-[0.25em] uppercase mb-8 text-primary">
                Common Inquiries
              </p>
              <h2 className="font-serif font-light text-foreground text-[2.5rem] md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight mb-8">
                Security,{" "}
                <span className="italic font-light text-secondary">
                  simplified.
                </span>
              </h2>
              <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mb-10 max-w-md">
                Answers to the most frequent questions about our VPN services. 
                If you need further help, our team is always here to assist you.
              </p>
              <Link to="/VpnGenixPage" className="font-default text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 inline-flex items-center gap-2 group">
                <span className="border-b border-muted-foreground/30 group-hover:border-foreground pb-1 transition-colors duration-300">
                  Need more help? Contact us
                </span>
                <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">
                  &rarr;
                </span>
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border last:border-b-0 group">
                  <AccordionTrigger className="font-serif text-xl md:text-[1.375rem] font-normal text-foreground hover:no-underline py-7 text-left leading-snug data-[state=open]:text-primary transition-colors duration-300 [&>svg]:hidden gap-6">
                    <span className="flex-1 pr-4">{faq.question}</span>
                    <span className="relative flex items-center justify-center size-6 shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-[135deg]">
                      <Plus className="size-5 text-muted-foreground group-data-[state=open]:text-primary transition-colors duration-300" strokeWidth={1.25} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="font-default text-base text-muted-foreground leading-[1.7] pb-7 pr-12 max-w-2xl">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>

        {}
        <div ref={closingRef} className={`mt-24 md:mt-32 transition-all duration-1000 ease-out ${closingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="w-full h-px mb-10 bg-border" />
          <p className="font-serif italic text-center text-lg md:text-xl font-light text-secondary">
            Your privacy is our priority.
          </p>
        </div>
      </div>
    </section>;
}